import { useCallback, useEffect, useState, type FormEvent } from "react";
import TextArea from "../../components/TextArea";
import Modal from "../../components/Modal";
import FormMessage from "../../components/FormMessage";
import Avatar from "../../components/Avatar";
import AdminBadge from "../../components/AdminBadge";
import { ApiError, apiFetch } from "../../lib/api";
import { useAuth, type PublicUser } from "../../lib/auth";

type Author = {
  id: string;
  name: string;
  photoUrl: string | null;
  role: "MEMBER" | "ADMIN";
};

type Comment = {
  id: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  author: Author;
};

type Post = {
  id: string;
  body: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  author: Author;
  comments: Comment[];
};

export default function Discussion() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ posts: Post[] }>("/api/posts");
      setPosts(data.posts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load posts.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: "1rem",
          flexWrap: "wrap",
          marginBottom: "2rem",
        }}
      >
        <div>
          <div className="eyebrow">Discussion</div>
          <h1
            className="display display--lg"
            style={{ marginTop: "0.5rem", marginBottom: 0 }}
          >
            the group chat.
          </h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setComposing(true)}
          >
            + New post
          </button>
        )}
      </header>

      {error && <FormMessage tone="error">{error}</FormMessage>}

      {!posts ? (
        <p className="eyebrow">Loading…</p>
      ) : posts.length === 0 ? (
        <p style={{ color: "var(--c-palm)", opacity: 0.7 }}>
          {isAdmin
            ? "No posts yet. Drop a thought to start the conversation."
            : "Nothing to discuss yet. An admin will kick things off soon."}
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "1.25rem" }}>
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              me={user!}
              isAdmin={isAdmin}
              onEdit={() => setEditingPost(p)}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}

      {(composing || editingPost) && (
        <PostFormModal
          initial={editingPost ?? undefined}
          onClose={() => {
            setComposing(false);
            setEditingPost(null);
          }}
          onSaved={async () => {
            setComposing(false);
            setEditingPost(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

// ---------- Post card ----------

const PREVIEW_WORDS = 100;

function truncateWords(text: string, maxWords: number) {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return { display: text, truncated: false };
  return {
    display: words.slice(0, maxWords).join(" ") + "…",
    truncated: true,
  };
}

function commentCountLabel(n: number): string {
  if (n === 0) return "No comments yet";
  if (n === 1) return "1 comment";
  if (n > 10) return "10+ comments";
  return `${n} comments`;
}

function PostCard({
  post,
  me,
  isAdmin,
  onEdit,
  onChanged,
}: {
  post: Post;
  me: PublicUser;
  isAdmin: boolean;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const { display, truncated } = truncateWords(post.body, PREVIEW_WORDS);
  const showFull = expanded || !truncated;

  async function togglePin() {
    await apiFetch(`/api/posts/${post.id}`, {
      method: "PATCH",
      body: { isPinned: !post.isPinned },
    });
    await onChanged();
  }
  async function deletePost() {
    if (!confirm("Delete this post?")) return;
    await apiFetch(`/api/posts/${post.id}`, { method: "DELETE" });
    await onChanged();
  }

  return (
    <li
      style={{
        background: "#fff",
        border: post.isPinned
          ? "2px solid var(--c-coral)"
          : "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "1.25rem",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "0.85rem",
        }}
      >
        <Avatar name={post.author.name} photoUrl={post.author.photoUrl} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: 600, color: "var(--c-palm)" }}>
              {post.author.name}
            </span>
            {post.author.role === "ADMIN" && <AdminBadge />}
            {post.isPinned && (
              <span
                className="eyebrow"
                style={{ color: "var(--c-coral)", fontSize: "0.62rem" }}
              >
                · pinned
              </span>
            )}
          </div>
          <div style={{ fontSize: "0.8rem", opacity: 0.6 }}>
            {new Date(post.createdAt).toLocaleString()}
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <SmallButton onClick={togglePin}>
              {post.isPinned ? "Unpin" : "Pin"}
            </SmallButton>
            <SmallButton onClick={onEdit}>Edit</SmallButton>
            <SmallButton tone="coral" onClick={deletePost}>
              Delete
            </SmallButton>
          </div>
        )}
      </header>

      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
        {showFull ? post.body : display}
      </div>

      {!showFull && (
        <div
          style={{
            marginTop: "1rem",
            paddingTop: "0.85rem",
            borderTop: "1px solid var(--c-rule)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <span
            className="eyebrow"
            style={{ color: "var(--c-palm)", fontSize: "0.7rem", opacity: 0.75 }}
          >
            {commentCountLabel(post.comments.length)}
          </span>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="btn btn--ghost"
            style={{ padding: "0.5rem 1rem", fontSize: "0.72rem" }}
          >
            Read more →
          </button>
        </div>
      )}

      {showFull && (
        <>
          <CommentList
            postId={post.id}
            comments={post.comments}
            me={me}
            isAdmin={isAdmin}
            onChanged={onChanged}
          />
          {truncated && (
            <div style={{ marginTop: "0.5rem", textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                style={{
                  background: "transparent",
                  padding: "0.4rem 0.6rem",
                  fontFamily: "var(--f-label)",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: "var(--c-palm)",
                  opacity: 0.7,
                }}
              >
                Show less
              </button>
            </div>
          )}
        </>
      )}
    </li>
  );
}

// ---------- Comments ----------

function CommentList({
  postId,
  comments,
  me,
  isAdmin,
  onChanged,
}: {
  postId: string;
  comments: Comment[];
  me: PublicUser;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await apiFetch(`/api/posts/${postId}/comments`, {
        method: "POST",
        body: { body: body.trim() },
      });
      setBody("");
      await onChanged();
    } finally {
      setSubmitting(false);
    }
  }

  async function removeComment(c: Comment) {
    if (!confirm("Delete this comment?")) return;
    await apiFetch(`/api/posts/comments/${c.id}`, { method: "DELETE" });
    await onChanged();
  }

  return (
    <div
      style={{
        marginTop: "1.25rem",
        paddingTop: "1rem",
        borderTop: "1px solid var(--c-rule)",
      }}
    >
      {comments.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1rem" }}>
          {comments.map((c) => {
            const canDelete = isAdmin || c.author.id === me.id;
            return (
              <li
                key={c.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: "0.65rem",
                  alignItems: "start",
                  padding: "0.5rem 0",
                }}
              >
                <Avatar
                  name={c.author.name}
                  photoUrl={c.author.photoUrl}
                  size={28}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--c-palm)" }}
                    >
                      {c.author.name}
                    </span>
                    {c.author.role === "ADMIN" && <AdminBadge />}
                    <span style={{ fontSize: "0.75rem", opacity: 0.55 }}>
                      {new Date(c.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: "0.95rem",
                      lineHeight: 1.5,
                      marginTop: 2,
                    }}
                  >
                    {c.body}
                  </div>
                </div>
                {canDelete && (
                  <SmallButton tone="coral" onClick={() => removeComment(c)}>
                    Delete
                  </SmallButton>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={submit} style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
        <input
          type="text"
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          placeholder="Add a comment…"
          maxLength={4000}
          style={{
            flex: 1,
            padding: "0.7rem 0.9rem",
            borderRadius: "var(--r-sm)",
            border: "1px solid var(--c-rule)",
            background: "#fff",
            fontSize: "0.95rem",
          }}
        />
        <button
          type="submit"
          className="btn btn--primary"
          disabled={submitting || !body.trim()}
          style={{ padding: "0.6rem 1rem", fontSize: "0.78rem" }}
        >
          Post
        </button>
      </form>
    </div>
  );
}

function SmallButton({
  children,
  onClick,
  tone = "palm",
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone?: "palm" | "coral";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "0.35rem 0.7rem",
        fontFamily: "var(--f-label)",
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        fontSize: "0.65rem",
        fontWeight: 700,
        color: tone === "coral" ? "var(--c-coral)" : "var(--c-palm)",
        background: "transparent",
        borderRadius: "var(--r-pill)",
      }}
    >
      {children}
    </button>
  );
}

// ---------- Post form modal ----------

function PostFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Post;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [body, setBody] = useState(initial?.body ?? "");
  const [isPinned, setIsPinned] = useState(initial?.isPinned ?? false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (initial) {
        await apiFetch(`/api/posts/${initial.id}`, {
          method: "PATCH",
          body: { body: body.trim(), isPinned },
        });
      } else {
        await apiFetch("/api/posts", {
          method: "POST",
          body: { body: body.trim(), isPinned },
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? "Edit post" : "New post"}
      maxWidth={620}
    >
      {error && <FormMessage tone="error">{error}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <TextArea
          label="Post"
          required
          rows={8}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          maxLength={8000}
          autoFocus
        />
        <label
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            marginBottom: "1rem",
            fontSize: "0.9rem",
          }}
        >
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.currentTarget.checked)}
            style={{ accentColor: "var(--c-coral)", width: 18, height: 18 }}
          />
          Pin to top
        </label>
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn--ghost"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting || !body.trim()}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
